import { CartAddEvent } from '@theme/events';

/**
 * <often-bought-together> — Amazon-style product-page bundle ("Often bought
 * together"). Native, no app.
 *
 * The markup is server-rendered by snippets/often-bought-together.liquid: the
 * product being viewed (the "anchor", marked [data-anchor]) plus manually
 * curated companion products, each a [data-obt-item] row with a checkbox and a
 * data-price (in cents). This component keeps a live combined total in sync as
 * boxes are toggled, and on "Add selected to cart" adds every checked item in a
 * single /cart/add.js request, then dispatches CartAddEvent so the theme morphs
 * the cart sections and opens the drawer (same mechanism as cart-upsell.js).
 *
 * Variant resolution per row:
 *   - anchor   → read live from the on-page product form (always matches the
 *                variant the shopper has selected in the buy-buttons picker).
 *   - single   → hidden input[data-obt-variant] with the only variant id.
 *   - multiple → select[data-obt-variant]; its <option> carries data-price so
 *                the total tracks the chosen variant.
 */
class OftenBoughtTogether extends HTMLElement {
  connectedCallback() {
    this.totalEl = this.querySelector('[data-obt-total]');
    this.addBtn = this.querySelector('[data-obt-add]');
    this.rows = [...this.querySelectorAll('[data-obt-item]')];

    this.addBtn?.addEventListener('click', this.#onAdd);

    this.rows.forEach((row) => {
      row.querySelector('[data-obt-check]')?.addEventListener('change', this.#syncRow.bind(this, row));
      row.querySelector('select[data-obt-variant]')?.addEventListener('change', this.#onVariantChange);
    });

    this.#updateTotal();
  }

  #onVariantChange = (event) => {
    const select = event.currentTarget;
    const option = select.options[select.selectedIndex];
    const row = select.closest('[data-obt-item]');
    if (row && option?.dataset.price) row.dataset.price = option.dataset.price;
    this.#updateTotal();
  };

  #syncRow(row) {
    const checked = row.querySelector('[data-obt-check]')?.checked;
    row.classList.toggle('obt__item--unchecked', !checked);
    this.#updateTotal();
  }

  #updateTotal = () => {
    let cents = 0;
    let count = 0;
    this.rows.forEach((row) => {
      if (row.querySelector('[data-obt-check]')?.checked) {
        cents += Number(row.dataset.price) || 0;
        count += 1;
      }
    });
    if (this.totalEl) this.totalEl.textContent = this.#money(cents);
    if (this.addBtn) this.addBtn.disabled = count === 0;
  };

  #onAdd = async () => {
    const items = [];
    for (const row of this.rows) {
      if (!row.querySelector('[data-obt-check]')?.checked) continue;
      const id = row.hasAttribute('data-anchor')
        ? this.#anchorVariantId()
        : row.querySelector('[data-obt-variant]')?.value;
      if (id) items.push({ id: Number(id), quantity: 1 });
    }
    if (!items.length) return;

    this.addBtn.disabled = true;
    this.addBtn.classList.add('obt__cta--loading');

    try {
      const sectionIds = [...document.querySelectorAll('cart-items-component')]
        .map((node) => node.dataset.sectionId)
        .filter(Boolean);

      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });

      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'add failed');
      }

      document.dispatchEvent(
        new CartAddEvent(data, 'often-bought-together', {
          source: 'often-bought-together',
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error('[often-bought-together] add', error);
    } finally {
      this.addBtn.disabled = false;
      this.addBtn.classList.remove('obt__cta--loading');
    }
  };

  /** Live variant id from the product form, so the anchor matches the picker. */
  #anchorVariantId() {
    const input =
      document.querySelector('product-form-component form input[name="id"]') ||
      document.querySelector('form[action*="/cart/add"] input[name="id"]') ||
      document.querySelector('input[name="id"]');
    return input?.value || null;
  }

  #money(cents) {
    const amount = (Number(cents) || 0) / 100;
    const currency = window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
    } catch {
      return `$${amount.toFixed(2)}`;
    }
  }
}

if (!customElements.get('often-bought-together')) {
  customElements.define('often-bought-together', OftenBoughtTogether);
}
